import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Person, LabelsByLevel, FamilyContext, FormTabType } from "../types";
import { maskCPF, maskPhone, isValidCPF, isValidEmail, isValidPhone } from "@/utils/maskUtils";
import TabNavigation from "./TabNavigation";
import PersonNavigation from "./PersonNavigation";
import LegalRepresentativeForm from "./LegalRepresentativeForm";
import { getLabelsByLevel, updatePersonAtPath } from "../utils/personUtils";

interface PersonFormProps {
  person: Person;
  onChange: (updatedPerson: Person) => void;
  context: FamilyContext;
  onContextChange: (newContext: Partial<FamilyContext>) => void;
  showLegalRepresentative?: boolean;
}

const PersonForm: React.FC<PersonFormProps> = ({
  person,
  onChange,
  context,
  onContextChange,
  showLegalRepresentative = true
}) => {
  const { level, path, activeTab } = context;
  
  // Obter labels com base no nível atual
  const labels = getLabelsByLevel(level);
  
  // Determinar se este nível pode ter filhos (não permitir filhos de bisnetos)
  const canHaveChildren = level !== "GREATGRANDCHILD";
  
  // Obter o índice atual do filho/neto/bisneto
  const currentIndex = path[0] || 0;
  
  // Obter a pessoa atual com base na aba ativa
  const getCurrentPersonByTab = (): Person => {
    if (activeTab === "PERSONAL") {
      return person || {};
    } else if (activeTab === "COUPLE") {
      if (!person.couple) {
        return {};
      }
      return person.couple[0] || {};
    }
    return {};
  };
  
  // Atualizar pessoa atual com base na aba ativa
  const updateCurrentPersonByTab = (updatedPerson: Person) => {
    if (activeTab === "PERSONAL") {
      onChange(updatedPerson);
    } else if (activeTab === "COUPLE") {
      // Garantir que existe um cônjuge
      if (!person.couple) {
        person.couple = [{}];
      }
      onChange({
        ...person,
        couple: [updatedPerson]
      });
    }
  };
  
  // Atualizar campo da pessoa
  const handleFieldChange = (field: keyof Person, value: string) => {
    const currentPerson = getCurrentPersonByTab();
    updateCurrentPersonByTab({
      ...currentPerson,
      [field]: value
    });
  };
  
  // Adicionar filho
  const handleAddChild = () => {
    const children = person.children || [];
    const newChildren = [...children, [{}]];
    
    onChange({
      ...person,
      children: newChildren
    });
    
    // Atualizar caminho para selecionar o novo filho e garantir que a aba ativa continua sendo CHILD
    onContextChange({ 
      path: [newChildren.length - 1, ...path.slice(1)],
      activeTab: "CHILD" // Força a aba a permanecer em CHILD
    });
  };
  
  // Remover filho
  const handleRemoveChild = (index: number) => {
    const children = person.children || [];
    if (!children.length) return;
    
    const newChildren = children.filter((_, idx) => idx !== index);
    
    onChange({
      ...person,
      children: newChildren.length ? newChildren : undefined
    });
    
    // Ajustar índice do filho ativo
    if (index >= newChildren.length && newChildren.length > 0) {
      onContextChange({ 
        path: [Math.max(0, newChildren.length - 1), ...path.slice(1)]
      });
    }
  };
  
  // Atualizar filho selecionado
  const handleChildUpdate = (updatedChild: Person) => {
    const children = person.children || [];
    if (!children.length || currentIndex >= children.length) return;
    
    const newChildren = [...children];
    newChildren[currentIndex] = [updatedChild];
    
    onChange({
      ...person,
      children: newChildren
    });
  };
  
  // Determinar o próximo nível baseado no nível atual
  const getNextLevel = (): "CHILD" | "GRANDCHILD" | "GREATGRANDCHILD" => {
    if (level === "MAIN") return "CHILD";
    if (level === "CHILD") return "GRANDCHILD";
    return "GREATGRANDCHILD";
  };
  
  // Estado para rastrear se o formulário foi tocado/tentativa de submissão
  const [touched, setTouched] = useState<boolean>(false);
  
  // Tocar todos os campos ao tentar navegar
  React.useEffect(() => {
    // Verificar se o componente pai está tentando validar/navegar
    const personComplete = person && person.name && person.email && person.cpf && person.celphone;
    if (!personComplete && person && Object.keys(person).length > 0) {
      setTouched(true);
    }
  }, [person]);
  
  // Função para verificar se algum campo do cônjuge está preenchido
  const hasAnySpouseField = (): boolean => {
    if (!person.couple || !person.couple[0]) return false;
    const spouse = person.couple[0];
    return !!(spouse.name || spouse.email || spouse.cpf || spouse.celphone);
  };
  
  // Função para validar campos específicos
  const validateField = (field: keyof Person, value: string | undefined): boolean => {
    if (!value) return false;
    
    switch (field) {
      case 'name':
        // Nome deve ter pelo menos 3 caracteres
        return value.trim().length >= 3;
      case 'email':
        return isValidEmail(value);
      case 'cpf':
        return isValidCPF(value);
      case 'celphone':
        return isValidPhone(value);
      default:
        return true;
    }
  };
  
  // Função para verificar se um campo está em erro
  const isFieldInError = (field: keyof Person): boolean => {
    const currentPerson = getCurrentPersonByTab();
    const value = currentPerson[field];
    
    // Lógica especial para cônjuge: se qualquer campo estiver preenchido, todos são obrigatórios
    if (activeTab === "COUPLE") {
      if (!hasAnySpouseField()) return false;
      if (!value) return touched;
      return touched && !validateField(field, value as string);
    }
    
    // Lógica padrão para outros tipos de pessoa
    if (!value) return touched;
    return touched && !validateField(field, value as string);
  };
  
  // Função para obter mensagem de erro específica para cada campo
  const getFieldErrorMessage = (field: keyof Person): string => {
    const currentPerson = getCurrentPersonByTab();
    const value = currentPerson[field];
    
    if (!value) return "Campo obrigatório";
    
    switch (field) {
      case 'name':
        return "Nome deve ter pelo menos 3 caracteres";
      case 'email':
        return "Email inválido";
      case 'cpf':
        return "CPF inválido";
      case 'celphone':
        return "Telefone inválido";
      default:
        return "Campo inválido";
    }
  };
  
  // Função para obter a classe de estilo do input baseada no erro
  const getInputClassName = (field: keyof Person): string => {
    const baseClass = "w-full placeholder:text-background/60 border-0 border-b rounded-none focus-visible:ring-0";
    const errorClass = isFieldInError(field) ? "border-b-red-500" : "border-b-background";
    return `${baseClass} ${errorClass}`;
  };
  
  // Renderizar o formulário pessoal ou formulário de filhos
  const renderContent = () => {
    const currentPerson = getCurrentPersonByTab();
    
    // Se estiver na aba de filhos, renderizar o componente filho
    if (activeTab === "CHILD") {
      if (!canHaveChildren) return null;
      
      const children = person.children || [];
      const selectedChild = children[currentIndex]?.[0] || {};
      
      return (
        <div>
          {/* Navegação entre filhos deste nível */}
          <PersonNavigation
            items={children}
            activeIndex={currentIndex}
            onSelectIndex={(idx) => {
              // Ao mudar para um novo filho/neto, manter a aba ativa do nível atual
              // Isso permite que permaneçamos na aba CHILD se estivermos navegando entre netos
              onContextChange({ 
                path: [idx, ...path.slice(1)]
              });
            }}
            onAddItem={handleAddChild}
            onRemoveItem={handleRemoveChild}
            addButtonLabel={labels.addPerson}
          />
          
          {/* Mostrar nome da pessoa selecionada quando há mais de um */}
          {children.length > 1 && selectedChild.name && (
            <p className="flex items-center w-full my-4 text-background">
              {selectedChild.name}
            </p>
          )}
          
          {children.length > 0 && (
            <PersonForm
              person={selectedChild}
              onChange={handleChildUpdate}
              context={{
                level: getNextLevel(),
                path: path.slice(1),
                activeTab: level === "MAIN" ? (context.childTab || "PERSONAL") :
                          level === "CHILD" ? (context.grandChildTab || "PERSONAL") :
                          (context.greatGrandChildTab || "PERSONAL"),
                childTab: context.childTab,
                grandChildTab: context.grandChildTab,
                greatGrandChildTab: context.greatGrandChildTab
              }}
              onContextChange={(newChildContext) => {
                // Mapear mudanças do contexto filho para o contexto pai
                if (newChildContext.path) {
                  onContextChange({ 
                    path: [currentIndex, ...newChildContext.path] 
                  });
                }
                if (newChildContext.activeTab) {
                  // Quando um nível altera sua aba ativa, precisamos preservar essa informação
                  // Isso assegura que os níveis filhos possam voltar à sua aba quando o usuário retornar
                  const pathKey = level === "MAIN" ? "childTab" : 
                                 level === "CHILD" ? "grandChildTab" : 
                                 "greatGrandChildTab";
                                   
                  // Se estamos no nível filho ou neto e a aba ativa é CHILD, 
                  // precisamos propagar a mudança para o formulário pai também
                  if (level !== "MAIN" && newChildContext.activeTab === "CHILD") {
                    // Atualizar a aba do nível atual para CHILD para permitir navegação
                    onContextChange({ 
                      [pathKey]: newChildContext.activeTab,
                      activeTab: "CHILD" 
                    });
                  } else {
                    // Caso contrário, apenas atualizamos a aba do nível filho
                    onContextChange({ [pathKey]: newChildContext.activeTab });
                  }
                }
                
                // Propagar outras alterações de contexto (como childTab, grandChildTab, etc.)
                if (newChildContext.childTab) {
                  onContextChange({ childTab: newChildContext.childTab });
                }
                if (newChildContext.grandChildTab) {
                  onContextChange({ grandChildTab: newChildContext.grandChildTab });
                }
                if (newChildContext.greatGrandChildTab) {
                  onContextChange({ greatGrandChildTab: newChildContext.greatGrandChildTab });
                }
              }}
            />
          )}
        </div>
      );
    }
    
    // Renderizar formulário pessoal
    return (
      <div className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Nome{isFieldInError('name') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input 
              className={getInputClassName('name')}
              placeholder="Digite o nome completo"
              value={currentPerson.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
            />
            {isFieldInError('name') && (
              <p className="text-red-500 text-xs mt-1">{getFieldErrorMessage('name')}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Email{isFieldInError('email') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input 
              className={getInputClassName('email')}
              placeholder="exemplo@email.com"
              value={currentPerson.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
            />
            {isFieldInError('email') && (
              <p className="text-red-500 text-xs mt-1">{getFieldErrorMessage('email')}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              CPF{isFieldInError('cpf') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input 
              className={getInputClassName('cpf')}
              placeholder="000.000.000-00"
              value={currentPerson.cpf || ''}
              onChange={(e) => handleFieldChange('cpf', maskCPF(e.target.value))}
            />
            {isFieldInError('cpf') && (
              <p className="text-red-500 text-xs mt-1">{getFieldErrorMessage('cpf')}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Celular{isFieldInError('celphone') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input 
              className={getInputClassName('celphone')}
              placeholder="(00) 00000-0000"
              value={currentPerson.celphone || ''}
              onChange={(e) => handleFieldChange('celphone', maskPhone(e.target.value))}
            />
            {isFieldInError('celphone') && (
              <p className="text-red-500 text-xs mt-1">{getFieldErrorMessage('celphone')}</p>
            )}
          </div>
        </div>
        
        {/* Representante legal */}
        {showLegalRepresentative && (
          <LegalRepresentativeForm
            person={currentPerson}
            onChange={updateCurrentPersonByTab}
          />
        )}
      </div>
    );
  };

  // Configurar abas disponíveis (não mostrar "CHILD" para bisnetos)
  const availableTabs: FormTabType[] = canHaveChildren 
    ? ["PERSONAL", "COUPLE", "CHILD"] 
    : ["PERSONAL", "COUPLE"];

  return (
    <div>
      {/* Navegação entre Pessoa/Cônjuge/Filhos */}
      <nav className="flex justify-center items-center gap-4 w-full mb-4">
        <TabNavigation 
          activeTab={activeTab} 
          onChangeTab={(tab) => onContextChange({ activeTab: tab })} 
          availableTabs={availableTabs}
          tabLabels={{
            PERSONAL: labels.person,
            COUPLE: labels.couple,
            CHILD: labels.children
          }}
          person={person}
        />
      </nav>
      
      {/* Conteúdo baseado na aba ativa */}
      <div className="py-4 text-xs sm:text-base">
        {renderContent()}
      </div>
    </div>
  );
};

export default PersonForm; 